import { computed, watch } from 'vue'
import { useShellStore, type ModuleAttention } from '@bridge2ai/core/frontend'
import type { Confirmation } from './types'
import { reviewLineCount } from './flagFilter'
import { usePoConfirmations } from './composables/usePoConfirmations'

/** Records a person is done with — nothing left to review on those. */
const SETTLED = new Set<Confirmation['status']>(['applied', 'confirmed_as_ordered'])

/** Discrepancy flags still open across the inbox: the list's Flags column summed over unsettled records. */
export function openFlagCount(list: Confirmation[]): number {
  return list.filter((c) => !SETTLED.has(c.status)).reduce((n, c) => n + reviewLineCount(c), 0)
}

/** What the PO Confirmations sidebar badge shows (owner, 2026-09-11: "the amount of discrepancy flags"). */
export function poAttention(list: Confirmation[]): ModuleAttention {
  const count = openFlagCount(list)
  return { count, label: count === 1 ? 'discrepancy flag' : 'discrepancy flags' }
}

/** The mailbox poller's cadence — a new confirmation cannot appear more often than this. */
const REFRESH_MS = 5 * 60 * 1000

/**
 * Keep the PO Confirmations badge current without the module being open — the Production Scheduler's
 * overdue-orders badge registers from inside the module, but nobody lands on PO Confirmations, so this source
 * runs at app level (see main.ts) against the module's shared record list: the page's own actions (apply,
 * recheck, upload, poll) update the same list, and the badge follows immediately. Returns a stop function.
 */
export function startPoAttention(): () => void {
  const shell = useShellStore()
  const { confirmations, refreshQuiet } = usePoConfirmations()
  const stop = watch(
    computed(() => poAttention(confirmations.value)),
    (a) => shell.setModuleAttention('poConfirmations', a),
    { immediate: true },
  )
  const tick = () => { void refreshQuiet() }
  tick()
  const timer = setInterval(tick, REFRESH_MS)
  const onVisible = () => { if (document.visibilityState === 'visible') tick() }
  document.addEventListener('visibilitychange', onVisible)
  return () => { stop(); clearInterval(timer); document.removeEventListener('visibilitychange', onVisible) }
}
